import { createContext, useContext, useEffect } from "react";
import { useLocalForage } from "~served/ui-comps/hooks/use-local-forage";
import { SHARED_STORAGE_KEYS } from "~served/ui-comps/storage-keys";
import { AppThemeProvider } from "~served/ui-comps/themes";
import { useInitApp } from "~/hooks/use-init-app";
import { getHubGqlWsClient } from "~/libs/gql";
import {
	clearSharedWsClient,
	registerSharedSubscription,
	setSharedWsClient,
} from "~/libs/ws-subscription-manager";
import { useGetCurrentVenue } from "~/queries/use-get-current-venue";
import { subscribeToCurrentDevice } from "~/queries/use-subscribe-to-current-device";
import { subscribeToDeviceOrders } from "~/queries/use-subscribe-to-device-orders";
import { subscribeToMenu } from "~/queries/use-subscribe-to-current-menu";
import { subscribeToCurrentVenue } from "~/queries/use-subscribe-to-current-venue";
import { subscribeToLocation } from "~/queries/use-subscribe-to-location";
import { subscribeToOrder } from "~/queries/use-subscribe-to-order";
import { subscribeToSystemHealth } from "~/queries/use-subscribe-to-system-health";
import {
	useDeviceId,
	useLocationId,
	useMenuId,
	useOrderId,
	useOrderRef,
	useToken,
	useVenueId,
} from "~/store";

const ColorModeContext = createContext<() => void>(() => undefined);
export const useToggleColorMode = () => useContext(ColorModeContext);

export const AppProvider = ({ children }) => {
	useInitApp();
	useWsSubscriptions();
	useClarityTags();

	const { data } = useGetCurrentVenue();
	const [isDarkMode, setIsDarkMode] = useLocalForage(
		SHARED_STORAGE_KEYS.DARK_MODE,
		false,
	);

	const toggleColorMode = () => setIsDarkMode((prev) => !prev);

	return (
		<ColorModeContext.Provider value={toggleColorMode}>
			<AppThemeProvider
				mode={isDarkMode ? "dark" : "light"}
				venueTheme={data?.theme}>
				{children}
			</AppThemeProvider>
		</ColorModeContext.Provider>
	);
};

const useClarityTags = () => {
	const venueId = useVenueId();
	const locationId = useLocationId();
	const deviceId = useDeviceId();

	useEffect(() => {
		if (!window.clarity) return;
		if (venueId) window.clarity("set", "venueId", venueId);
	}, [venueId]);

	useEffect(() => {
		if (!window.clarity) return;
		if (locationId) window.clarity("set", "locationId", locationId);
	}, [locationId]);

	useEffect(() => {
		if (!window.clarity) return;
		if (deviceId) window.clarity("set", "deviceId", deviceId);
	}, [deviceId]);
};

const useWsSubscriptions = () => {
	const token = useToken();
	const venueId = useVenueId();
	const locationId = useLocationId();
	const menuId = useMenuId();
	const orderId = useOrderId();
	const orderRef = useOrderRef();
	const deviceId = useDeviceId();

	useEffect(() => {
		// Effect-local so a cleanup can only ever dispose its own client.
		const client = getHubGqlWsClient();
		setSharedWsClient(client);

		return () => {
			clearSharedWsClient(client);
			client.dispose();
		};
	}, [token, venueId, locationId, deviceId]);

	useEffect(
		() => registerSharedSubscription("system-health", subscribeToSystemHealth),
		[],
	);

	useEffect(() => {
		if (!deviceId) return;
		return registerSharedSubscription(
			`current-device:${deviceId}`,
			subscribeToCurrentDevice,
		);
	}, [deviceId]);

	useEffect(() => {
		if (!deviceId) return;
		return registerSharedSubscription(
			`device-orders:${deviceId}`,
			subscribeToDeviceOrders,
		);
	}, [deviceId]);

	useEffect(() => {
		if (!token || !venueId) return;
		return registerSharedSubscription(
			`current-venue:${venueId}`,
			subscribeToCurrentVenue,
		);
	}, [token, venueId]);

	useEffect(() => {
		if (!token || !locationId) return;
		return registerSharedSubscription(`location:${locationId}`, (client) =>
			subscribeToLocation(client, locationId),
		);
	}, [token, locationId]);

	useEffect(() => {
		if (!token || !menuId) return;
		return registerSharedSubscription(`menu:${menuId}`, (client) =>
			subscribeToMenu(client, menuId),
		);
	}, [token, menuId]);

	useEffect(() => {
		if (!orderId) return;
		return registerSharedSubscription(`order-id:${orderId}`, (client) =>
			subscribeToOrder(client, { orderId }),
		);
	}, [orderId]);

	useEffect(() => {
		if (!orderRef) return;
		return registerSharedSubscription(`order-ref:${orderRef}`, (client) =>
			subscribeToOrder(client, { orderRef }),
		);
	}, [orderRef]);
};

declare global {
	interface Window {
		clarity?: (command: "set", key: string, value: string) => void;
	}
}
